import type { Page, SuccessPhpResponse } from '@/types';
import type { Answers } from '@/redux/slider';
import type { AxiosResponse } from 'axios';
import sha256 from 'sha256';
import { v4 as uuidv4 } from 'uuid';
import { ConfigSlice } from '@/redux/config';
import { store } from '@/redux/store';
import { getConversionValue } from '@/utils/conversion-value';

type MapOptimizelyEventToConvertEvent = Record<string, Partial<Record<Page[number], string>>>;
const MAP_OPTIMIZELY_EVENT_TO_CONVERT_EVENT: MapOptimizelyEventToConvertEvent = {
  einstieg_funnel: {
    bussgeldcheck: '100153779',
    passexperten: '100153662',
  },
  to_form: {
    bussgeldcheck: '100153780',
    passexperten: '100153665',
  },
  formular_submit: {
    bussgeldcheck: '100153781',
    passexperten: '100153773',
  },
  formular_submit_fail: {
    bussgeldcheck: '100153782',
    passexperten: '100153774',
  },
  lead: {
    bussgeldcheck: '100153783',
    passexperten: '100153775',
  },
  prospect: {
    passexperten: '',
  },
  leadfunnelFileUploaded: {
    bussgeldcheck: '100153784',
    passexperten: '100153776',
  },
  no_document_upload: {
    bussgeldcheck: '100153785',
    passexperten: '100153777',
  },
  additionalQuestionsSubmit: {
    bussgeldcheck: '100153786',
    passexperten: '100153778',
  },
  long_read_cta_click: {
    bussgeldcheck: '100153787',
  },
  long_read_cta_with_lci: {
    bussgeldcheck: '100153788',
  },
  long_read_cta_without_lci: {
    bussgeldcheck: '100153789',
  },
  modal_cta_with_lci: {
    bussgeldcheck: '100153790',
  },
  modal_cta_without_lci: {
    bussgeldcheck: '100153791',
  },
};

const MAP_OPTIMIZELY_PAGE_TO_CONVERT_PAGE = ['bussgeldcheckManualActivation', 'longreadManualActivation'] as const;
type ConvertManualPage = (typeof MAP_OPTIMIZELY_PAGE_TO_CONVERT_PAGE)[number];

abstract class Pushable<TPayload> {
  protected abstract Push(data: TPayload): void;
}

class ErrorTracker extends Pushable<string> {
  protected Push(message: string) {
    if (!window.trackJs) return;
    window.trackJs.track(message);
  }

  public Api(response?: AxiosResponse) {
    if (response) {
      const status = response?.status || '[not provided]';
      const email = response?.data?.email || '[not provided]';
      const data = JSON.stringify(response.data);
      const headers = JSON.stringify(response.headers);
      this.Push(`Status: [${status}]. Email: ${email}. Data: ${data}. Headers: ${headers}.`);
      this.Push(`Lead backup: ${email}. Body: ${data}/`);
    } else {
      this.Push('Request response is undefined.');
    }
  }

  public NoResponse(request: unknown) {
    if (!request) {
      this.Push(`No response was received. Request: ${JSON.stringify(request)}.`);
    } else {
      this.Push('No response was received. Request is undefined.');
    }
  }

  public Unhandled(message?: string) {
    if (message) {
      this.Push(`Unhandled error. Message: ${message}.`);
    } else {
      this.Push('Unhandled error. Message is undefined.');
    }
  }

  public PostFailed(endpoint: string) {
    this.Push(`Fail to POST[${endpoint}]`);
  }
}

class DataLayerTracker extends Pushable<DataLayer> {
  protected Push(data: DataLayer) {
    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push(data);
  }

  private PushRid(data: SuccessPhpResponse) {
    if (!data.rid) return;
    this.Push({ rid: data.rid });
  }

  private PushLeadId(data: SuccessPhpResponse) {
    if (!data.leadId) return;
    this.Push({ leadId: data.leadId });
  }

  private PushUserForm(data: SuccessPhpResponse) {
    const trimEmail = (email: string): string => {
      const trimmedEmail = email.trim();
      const gmailRegex = /@(gmail|googlemail)\.[a-z.]{2,}$/;
      if (gmailRegex.test(trimmedEmail)) {
        const [localPart, domain] = trimmedEmail.split('@');
        return localPart.replace(/\./g, '') + '@' + domain;
      }
      return trimmedEmail;
    };

    const lowerCaseWithFallback = (str?: string): string => {
      if (!str) return '';
      return str.toLowerCase();
    };

    this.Push({
      event: 'formSubmit',
      salutation: sha256(lowerCaseWithFallback(data.salutation)),
      first_name: sha256(lowerCaseWithFallback(data.first_name)),
      last_name: sha256(lowerCaseWithFallback(data.last_name)),
      phone: sha256(lowerCaseWithFallback(data.phone)),
      email: sha256(trimEmail(lowerCaseWithFallback(data.email))),
      zip: sha256(lowerCaseWithFallback(data.zip)),
    });
  }

  private PushRandomizer(data: SuccessPhpResponse) {
    this.Push({ randomizer: data.randomizer || '-2' });
  }

  private PushThankYouConversion() {
    this.Push({ event: 'thankyou_conversion' });
  }

  private PushProspectConversion() {
    this.Push({ event: 'prospect_conversion' });
    this.Push({
      event: 'GAEvent',
      eventCategory: 'form',
      eventAction: 'prospect-success',
      eventLabel: 'master',
      eventValue: '',
    });
  }

  /**
   * Fired once success.php responded 200 for a (non-prospect) lead. `eventValue` carries the lead's
   * conversion value by prio for pages with a mapping (see utils/conversion-value.ts), otherwise ''.
   */
  private PushSubmissionSuccess(data: SuccessPhpResponse) {
    const page = ConfigSlice.selectors.page(store.getState());
    this.Push({
      event: 'GAEvent',
      eventCategory: 'form',
      eventAction: 'submission-success',
      eventLabel: 'master',
      eventValue: getConversionValue(page, data),
    });
  }

  public SubmitFormSuccess() {
    this.Push({
      event: 'GAEvent',
      eventCategory: 'form',
      eventAction: 'valid-click',
      eventLabel: 'master',
      eventValue: '',
    });
  }

  public SubmitFormFailure() {
    this.Push({
      event: 'GAEvent',
      eventCategory: 'form',
      eventAction: 'not-valid-click',
      eventLabel: 'master',
      eventValue: '',
    });
  }

  public AdditionalTermsAccepted() {
    this.Push({
      event: 'GAEvent',
      eventCategory: 'form',
      eventAction: 'datenverarbeitungsmodal-click',
      eventLabel: '',
      eventValue: '',
    });
  }

  public LongReadClick() {
    this.Push({
      event: 'GAEvent',
      eventCategory: 'long-read',
      eventAction: 'contact-click',
    });
  }

  public ContactFormSent() {
    this.Push({
      event: 'GAEvent',
      eventCategory: 'long-read',
      eventAction: 'contact-send',
    });
  }

  public NotSuccessfulSubmit() {
    this.Push({
      event: 'GAEvent',
      eventCategory: 'form',
      eventAction: 'not-successful-submit',
      eventLabel: 'master',
      eventValue: '',
    });
  }

  public MarketingConversion() {
    this.Push({ event: 'marketing_conversion' });
  }

  public AccessZip() {
    this.Push({
      event: 'GAEvent',
      eventCategory: 'form',
      eventAction: 'access-zip',
      eventLabel: 'master',
      eventValue: '',
    });
  }

  public UuidV4() {
    this.Push({ event_id: uuidv4() });
  }

  public Question(eventAction: string, eventLabel: string) {
    this.Push({
      event: 'GAEvent',
      eventCategory: 'question',
      eventAction,
      eventLabel,
      eventValue: '1',
    });
  }

  public UploadSuccess() {
    this.Push({
      event: 'GAEvent',
      eventCategory: 'upload',
      eventAction: 'uploadSuccess',
      eventLabel: 'master',
      eventValue: '',
    });
  }

  public FileSizeRejected() {
    this.Push({
      event: 'GAEvent',
      eventCategory: 'upload',
      eventAction: 'fileSizeRejected',
      eventLabel: 'master',
      eventValue: '',
    });
  }

  public LongReadWhatsappClick() {
    this.Push({
      event: 'GAEvent',
      eventCategory: 'long-read',
      eventAction: 'whatsapp-click',
    });
  }

  public RueckversMoreInfoClick() {
    this.Push({
      event: 'GAEvent',
      eventCategory: 'price-modal',
      eventAction: 'rueckvers-more-info-click',
    });
  }

  public InterestDdvClick() {
    this.Push({
      event: 'GAEvent',
      eventCategory: 'price-modal',
      eventAction: 'intrest-ddv-click',
    });
  }

  public LeadfunnelFileUploaded(wasRejected: boolean) {
    this.Push({
      event: 'GAEvent',
      eventCategory: 'upload',
      eventAction: 'leadfunnelFileUploaded',
      eventLabel: wasRejected ? 'uploadWithFileSizeRejection' : 'UploadwithoutFileSizeRejection',
      eventValue: '',
    });
  }

  public NoDocumentUpload(wasRejected: boolean) {
    this.Push({
      event: 'GAEvent',
      eventCategory: 'upload',
      eventAction: 'NextWithoutUpload',
      eventLabel: wasRejected ? 'NextWithoutUploadWithRejection' : 'NextWithoutUpload',
      eventValue: '',
    });
  }

  public SubmitFormResponse(data: SuccessPhpResponse) {
    this.PushRid(data);
    this.PushLeadId(data);
    this.PushUserForm(data);
    this.PushRandomizer(data);
    if (data.brazeProspect) {
      this.PushProspectConversion();
    } else {
      // submission-success first, so thankyou_conversion picks up its eventValue via the dataLayer model
      this.PushSubmissionSuccess(data);
      this.PushThankYouConversion();
    }
  }
}

class ExperimentsTracker extends Pushable<Optimizely> {
  private startPageSent = false;
  private funnelIncomeSent = false;

  protected Push(data: Optimizely) {
    if (data.type === 'event') {
      const pageMap = MAP_OPTIMIZELY_EVENT_TO_CONVERT_EVENT[data.eventName];
      if (!pageMap) return;

      const currentPage = ConfigSlice.selectors.page(store.getState());
      const convertId = pageMap[currentPage];
      if (!convertId) return;

      this.PushToConvertEvent(convertId);
      return;
    }

    if (data.type === 'page') {
      if (!(MAP_OPTIMIZELY_PAGE_TO_CONVERT_PAGE as readonly string[]).includes(data.pageName)) return;
      this.PushToConvertPage(data.pageName as ConvertManualPage);
      return;
    }
  }

  private PushToConvertEvent(convertId: string) {
    window._conv_q = window._conv_q || [];
    window._conv_q.push(['triggerConversion', convertId]);
  }

  private PushToConvertPage(page: ConvertManualPage) {
    window._conv_q = window._conv_q || [];
    window._conv_q.push(['run', 'true']);
    window[page] = true;
  }

  public FunnelIncome() {
    if (this.funnelIncomeSent) return;
    this.funnelIncomeSent = true;
    this.Push({ type: 'event', eventName: 'einstieg_funnel' });
  }

  public SeePriceCtaButtonLongRead(answersDirect: Answers) {
    this.Push({ type: 'event', eventName: 'long_read_cta_click' });
    if (answersDirect && answersDirect['legal_insurance'] && answersDirect['legal_insurance'] === 'Ja') {
      this.Push({ type: 'event', eventName: 'long_read_cta_with_lci' });
    } else {
      this.Push({ type: 'event', eventName: 'long_read_cta_without_lci' });
    }
  }

  public SeePriceCtaButtonModal() {
    this.Push({ type: 'event', eventName: 'modal_cta_with_lci' });
  }

  public SeePriceAlternativeCtaButtonModal() {
    this.Push({ type: 'event', eventName: 'modal_cta_without_lci' });
  }

  public AdditionalQuestionsSubmit() {
    this.Push({ type: 'event', eventName: 'additionalQuestionsSubmit' });
  }

  public AdditionalQuestionsSubmitKey(key?: string) {
    this.Push({ type: 'event', eventName: `additionalQuestionsSubmit-${key}` });
  }

  public ToForm() {
    this.Push({ type: 'event', eventName: 'to_form' });
  }

  public FormularSubmit() {
    this.Push({ type: 'event', eventName: 'formular_submit' });
  }

  public FormularSubmitFail() {
    this.Push({ type: 'event', eventName: 'formular_submit_fail' });
  }

  public BussgeldcheckManualActivation() {
    this.Push({ type: 'page', pageName: 'bussgeldcheckManualActivation' });
  }

  public LongreadManualActivation() {
    this.Push({ type: 'page', pageName: 'longreadManualActivation' });
  }

  /**
   * Convert BYOID (Bring Your Own ID). Pushes our pre-generated visitor id onto Convert's
   * command queue so the snippet adopts it instead of minting one. Must be pushed BEFORE
   * Convert's tracking tag loads — otherwise Convert's very first "bucket" event is recorded
   * against an anonymous, Convert-generated id and BYOID stitching is lost.
   * See: https://convert.elevio.help/en/articles/86079-bring-your-own-id-byoid-use-your-visitorid-in-convert
   */
  public ConvertIdentify(visitorId: string) {
    window._conv_q = window._conv_q || [];
    window._conv_q.push({
      what: 'identify',
      params: { visitorId },
    });
  }

  /**
   * Convert equivalent of `Track.Experiments.Get('state').getCampaignStateLists({ isActive: true })`.
   * Reads the currently-active experience/variation map off `window.convert.currentData.experiences`
   * using Convert's native terminology: each record is `{ experienceId, variationId }`.
   * Reshaping to the Optimizely-compatible `{ campaignId, experimentId, variationId }` happens
   * at the cookie-write boundary in `Qid.updateCampaignsWithFreshConvertData()`.
   */
  public ConvertGetActiveExperiences(): Array<{ experienceId: string; variationId: string }> {
    try {
      const experiences = window.convert?.currentData?.experiences ?? {};
      return Object.entries(experiences).reduce<Array<{ experienceId: string; variationId: string }>>(
        (acc, [experienceId, exp]) => {
          const variationId = exp?.variation?.id;
          if (variationId) acc.push({ experienceId, variationId: String(variationId) });
          return acc;
        },
        [],
      );
    } catch {
      return [];
    }
  }

  /**
   * Convert equivalent of `OnOriginsSynced`. Registers a listener on
   * `snippet.experiences_evaluated`, which fires once all experiences are decided.
   * Pushed via the object command form (`{ what, params }`) per Convert's JavaScript API.
   * Visitor id is no longer captured here — it's supplied via BYOID (see ConvertAssignVisitorId).
   */
  // TODO: Do we need this action from where do we take it?
  public ConvertOnReady(handler: (...args: unknown[]) => void) {
    window._conv_q = window._conv_q || [];
    window._conv_q.push({
      what: 'addListener',
      params: { event: 'snippet.experiences_evaluated', handler },
    });
  }

  public pushLeadEvent() {
    this.Push({ type: 'event', eventName: 'lead' });
  }

  public pushProspectEvent() {
    this.Push({ type: 'event', eventName: 'prospect' });
  }

  public RueckversMoreInfoClick() {
    this.Push({ type: 'event', eventName: 'rueckvers-more-info-click' });
  }

  public InterestDdvClick() {
    this.Push({ type: 'event', eventName: 'intrest-ddv-click' });
  }

  public LeadfunnelFileUploaded() {
    this.Push({ type: 'event', eventName: 'leadfunnelFileUploaded' });
  }

  public NoDocumentUpload() {
    this.Push({ type: 'event', eventName: 'no_document_upload' });
  }

  /**
   * Convert's `['run', 'true']` re-evaluates and re-applies every experience, so variation JS runs
   * again on each push. Opt-in only: a Convert variation sets `window.convertStartPage` when it
   * actually needs that re-run, and without the flag nothing is pushed. The flag is set while the
   * snippet evaluates, which the mount gate guarantees happens before this is ever called.
   *
   * Latched on top of the flag: StrictMode double-invokes mount effects in dev and a remount would
   * repeat the push, and a variation that is not idempotent would duplicate its DOM changes or
   * listeners. `Track.Experiments` is a single instance, so the latch is page-scoped.
   */
  public StartPage() {
    if (!window.convertStartPage) return;
    if (this.startPageSent) return;
    this.startPageSent = true;
    window._conv_q = window._conv_q || [];
    window._conv_q.push(['run', 'true']);
  }
}

export default class Track {
  public static Error = new ErrorTracker();
  public static DataLayer = new DataLayerTracker();
  public static Experiments = new ExperimentsTracker();
}
